import { ShareSheetModal } from "@/components/modal/share-sheet/share-sheet-modal";
import { ShareSheetModalStore } from "@/components/modal/share-sheet/share-sheet-modal-store";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet/types";
import { MdsIconKind } from "@/design-system/components/icon";
import { AnimationTiming } from "@/design-system/constants/common";
import { withLineBreaks } from "@/domains/html/withLineBreaks";
import { EventContext } from "@/domains/metrics/context";
import { renderArrayWithAnds } from "@/domains/string/renderArrayWithAnds";
import { MDSIconButton, MDSTooltip, MDSTooltipPlacement, MDSIconButtonSize } from "@/mds";
import { INoteObservable } from "@/store/note/types";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";

interface NoteEditorTopBarButtonProps {
  note?: INoteObservable | TemplateObservable;
  shareSheetModal: ShareSheetModalStore;
}

export const NoteEditorTopBarShareButton = observer<NoteEditorTopBarButtonProps>(
  function NoteEditorTopBarShareButton({ note, shareSheetModal }) {
    const { isDisabled, tooltipLabel, iconKind } = computed(() => {
      if (!note?.canAccess) {
        return {
          isDisabled: true,
          tooltipLabel: "You lost access",
          iconKind: MdsIconKind.LockSolid,
        };
      }

      if (!note?.canWrite) {
        return {
          isDisabled: true,
          tooltipLabel: "You don't have write access",
          iconKind: MdsIconKind.Shared,
        };
      }

      if (note.hasPendingShare) {
        return {
          isDisabled: false,
          tooltipLabel: "Access changes will be applied when you’re online",
          iconKind: MdsIconKind.Spinner,
        };
      }

      if (note?.isShared) {
        const labels = note?.sharedWithLabels;
        if (labels) {
          return {
            isDisabled: false,
            tooltipLabel: withLineBreaks(
              `Shared with ${renderArrayWithAnds(
                labels.length >= 5 ? [...labels.slice(0, 3), `${labels.length - 2} others`] : labels
              )}`
            ),
            iconKind: MdsIconKind.Shared,
          };
        }
      }

      return {
        isDisabled: false,
        tooltipLabel: "Private to only me",
        iconKind: MdsIconKind.LockSolid,
      };
    }).get();

    const tooltipConfig = {
      label: tooltipLabel,
      placement: MDSTooltipPlacement.Top,
      delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
    };

    const onClick = useCallback(() => {
      if (!note?.id) return;
      shareSheetModal.open({
        id: note.id,
        entityKind:
          note instanceof TemplateObservable
            ? ShareSheetEntityKind.Template
            : ShareSheetEntityKind.Note,
        eventContext: EventContext.EditorActions,
      });
    }, [note, shareSheetModal]);

    return (
      <>
        <ShareSheetModal store={shareSheetModal} />
        <MDSTooltip config={tooltipConfig}>
          <MDSIconButton
            iconKind={iconKind}
            onClick={onClick}
            isDisabled={isDisabled}
            size={MDSIconButtonSize.Medium}
          />
        </MDSTooltip>
      </>
    );
  }
);
